import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import ListItem from "@mui/material/ListItem";

import { makeStyles } from "tss-react/mui";

import RotateRightIcon from "@mui/icons-material/RotateRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import CircularProgress from "@mui/material/CircularProgress";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import LoopOutlinedIcon from "@mui/icons-material/LoopOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

import Moment from "react-moment";
import { setProcessQueue, showToast } from "../../store/actions";
import ProcessQueueService from "../../service/ProcessQueueService";
import AwsService from "../../service/awsService";

const FileTypesToName = {
  1: "AWS S3",
  2: "Azure Blob",
  3: "Salesforce",
  4: "SFTP",
  5: "Sharepoint",
};

const useStyles = makeStyles()((theme) => ({
  processMenu: {
    marginTop: "20px",
    paddingTop: 0,
    "& .MuiList-root": {
      padding: 0,
    },
  },
  processMenuPaper: {
    height: "50px",
    width: "100%",
    padding: 0,
    display: "flex",
    alignItems: "center",
    borderRadius: "0px",
    position: "sticky",
    top: "0px",
    left: "0px",
    zIndex: "10000",
  },
  noProcessBox: {
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
    height: "70px",
    width: "100%",
  },
  processQueueAnimation: {
    color: "#0052CC",
    animation: "spin 5s linear infinite",
  },
  inProgressIcon: {
    animation: "processQueueInProgress 5s linear infinite",
  },
}));

const ProcessQueusList = ({ list, refreshProcessQueue }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const abortUpload = async (ProcessQueue_id) => {
    const response = await new AwsService().abortMultipartUpload({ ProcessQueue_id });

    if (response.success) {
      dispatch(
        showToast({
          message: response.message,
          type: "success",
        })
      );
    }

    refreshProcessQueue();
  };

  return list.map(({ ProcessQueue_id, Status, Detail, Message, FormatedDate }) => (
    <ListItem key={ProcessQueue_id} divider>
      <ListItemAvatar sx={{ p: 0, m: 0 }}>
        <Avatar sx={{ background: "transparent", opacity: 0.7 }}>
          {Status === 1 && (
            <LoopOutlinedIcon className={classes.inProgressIcon} sx={{ color: "#0000FF" }} fontSize="large" />
          )}
          {Status === 2 && <CheckCircleOutlineOutlinedIcon sx={{ color: "#00875A" }} fontSize="large" />}
          {Status === 3 && <ErrorOutlineOutlinedIcon sx={{ color: "#FF0000" }} fontSize="large" />}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={<Typography noWrap>{`Workspace: ${Detail.WSName ?? "-"}`}</Typography>}
        secondary={
          <>
            <Typography sx={{ display: "block" }} component="span" variant="body2" color="text.primary">
              Connection: {Detail.ConnectionName ?? "-"} ({FileTypesToName[Detail.ConnectionType] ?? ""})
            </Typography>
            <Typography
              component="span"
              variant="body2"
              color="text.primary"
              sx={{ ...(Status === 3 ? { color: "#FF0000" } : {}), wordBreak: "break-word" }}
            >
              {Message}
            </Typography>
          </>
        }
      />
      {Status === 1 && Detail?.UploadId && (
        <Tooltip title="Cancel Upload" arrow>
          <IconButton color="error" size="small" onClick={() => abortUpload(ProcessQueue_id)}>
            <HighlightOffIcon />
          </IconButton>
        </Tooltip>
      )}
      <Chip label={<Moment fromNow>{FormatedDate}</Moment>} sx={{ ml: "2px" }} />
    </ListItem>
  ));
};

const GearIcon = ({ inProgress }) => {
  const { classes } = useStyles();

  return (
    <Tooltip title="Process Queue" arrow>
      <RotateRightIcon
        sx={{ color: "#3a3d4a", width: 25, height: 25, "&:hover": { color: "#0052CC" } }}
        className={inProgress ? `${classes.processQueueAnimation}` : ""}
      />
    </Tooltip>
  );
};

function ProcessQueue() {
  const [processAnchorEl, setProcessAnchorEl] = useState(null);
  const [processQueueUpdating, setProcessQueueUpdating] = useState(false);

  const processQueue = useSelector((state) => state.processQueue);
  const dispatch = useDispatch();
  const { classes } = useStyles();

  const isQueueOpen = Boolean(processAnchorEl);
  const inProgress = useMemo(
    () =>
      processQueue.todayQueue.some((process) => process.Status === 1) ||
      processQueue.olderQueue.some((process) => process.Status === 1) ||
      processQueue.inProgress,
    [processQueue.inProgress, processQueue.olderQueue, processQueue.todayQueue]
  );

  const refreshProcessQueue = async () => {
    setProcessQueueUpdating(true);
    const queueService = new ProcessQueueService();
    const needOlderQueue =
      processQueue.olderQueue.length === 0 || processQueue.olderQueue.some((process) => process.Status === 1);
    const { data, success } = await queueService.getProcessQueue(needOlderQueue ? 1 : 0);
    if (success) {
      let inProgress = data.todayProcesses?.some(({ Status }) => Status === 1);
      if (needOlderQueue) inProgress = inProgress || data.olderProcesses?.some(({ Status }) => Status === 1);

      needOlderQueue
        ? dispatch(
            setProcessQueue({
              inProgress,
              todayQueue: data.todayProcesses ?? [],
              olderQueue: data.olderProcesses ?? [],
            })
          )
        : dispatch(setProcessQueue({ inProgress, todayQueue: data.todayProcesses ?? [] }));
    }
    setProcessQueueUpdating(false);
  };

  const handleQueueOpen = async (event) => {
    setProcessAnchorEl(event.currentTarget);
    await refreshProcessQueue();
  };

  const handleRefreshClick = async () => {
    await refreshProcessQueue();
  };

  const handleQueueClose = () => setProcessAnchorEl(null);

  useEffect(() => {
    const refreshProcessQueue = async (needOldQueue = 1) => {
      setProcessQueueUpdating(true);
      const queueService = new ProcessQueueService();
      const { data, success } = await queueService.getProcessQueue(needOldQueue);
      if (success) {
        let inProgress = data.todayProcesses?.some(({ Status }) => Status === 1);
        if (needOldQueue) inProgress = inProgress || data.olderProcesses?.some(({ Status }) => Status === 1);

        dispatch(
          setProcessQueue({
            todayQueue: data.todayProcesses ?? [],
            ...(needOldQueue ? { olderQueue: data.olderProcesses ?? [] } : {}),
            refresh: false,
            inProgress,
          })
        );
      }
      setProcessQueueUpdating(false);
    };

    if (processQueue.refresh) refreshProcessQueue(1);
    else if (processQueue.inProgress) refreshProcessQueue(0);
    return () => {};
  }, [dispatch, processQueue.inProgress, processQueue.refresh]);

  const renderProcessMenu = (
    <Menu
      anchorEl={processAnchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isQueueOpen}
      onClose={handleQueueClose}
      slotProps={{
        paper: {
          sx: {
            minWidth: "600px",
            maxWidth: "600px",
            minHeight: "80px",
            maxHeight: "700px",
            marginTop: "10px",
            borderRadius: "16px",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              borderRadius: "10px",
              marginTop: "50px",
              marginBottom: "16px",
            },
          },
        },
      }}
      className={classes.processMenu}
    >
      <Paper elevation={2} className={classes.processMenuPaper}>
        <Box
          sx={{
            padding: "10px",
            display: "flex",
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
            alignContent: "center",
          }}
        >
          <RotateRightIcon sx={{ color: "#3a3d4a", width: 25, height: 25, mr: "12px", ml: "4px" }} />
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: "1.1rem",
                fontWeight: "bold",
                width: "100%",
              }}
            >
              Process Queue
            </Typography>
          </Box>
          {processQueueUpdating ? (
            <CircularProgress sx={{ height: "24px !important", width: "24px !important" }} />
          ) : (
            <>
              <IconButton onClick={handleRefreshClick}>
                <RefreshIcon sx={{ width: 24, height: 24 }} />
              </IconButton>
            </>
          )}
        </Box>
      </Paper>
      {Object.keys(processQueue).length === 0 ||
      (processQueue?.todayQueue?.length === 0 && processQueue?.olderQueue?.length === 0) ? (
        <Box className={classes.noProcessBox}>You don't have any Process Queue notifications.</Box>
      ) : (
        <List>
          {processQueue?.todayQueue?.length !== 0 ? (
            <>
              <ListSubheader>Today</ListSubheader>
              <ProcessQueusList refreshProcessQueue={refreshProcessQueue} list={processQueue.todayQueue} />
            </>
          ) : null}
          {processQueue?.olderQueue?.length !== 0 ? (
            <>
              <ListSubheader>Older</ListSubheader>
              <ProcessQueusList refreshProcessQueue={refreshProcessQueue} list={processQueue.olderQueue} />
            </>
          ) : null}
          <Paper
            sx={{
              display: "flex",
              justifyContent: "center",
              alignContent: "center",
              alignItems: "center",
              height: "50px",
              width: "100%",
            }}
            elevation={3}
          >
            That's all your Process Queue notifications from last 30 days.
          </Paper>
        </List>
      )}
    </Menu>
  );

  return (
    <>
      <IconButton
        data-id="ProcessQueueIcon"
        color="inherit"
        size="large"
        edge="start"
        aria-label="ProcessQueue"
        aria-haspopup="true"
        onClick={handleQueueOpen}
        sx={{ mr: 1 }}
      >
        <Badge
          overlap="circular"
          badgeContent=" "
          variant="dot"
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          className="processqueue"
        >
          <GearIcon inProgress={inProgress} />
        </Badge>
      </IconButton>
      {isQueueOpen ? renderProcessMenu : null}
    </>
  );
}

export default React.memo(ProcessQueue);
